import { YES_NO, type Menu, type MenuOption } from '@/speech/control/decide';
import {
  CODENAMES,
  GEAR_COLOURS,
  HAIR_COLOURS,
  ITEMS,
  type LayerId,
  type LookItem,
} from './agentLook';
import { NAV, STEP_LINES, STEPS, stepKey, type Step } from './agentBuilder';
import { floors } from './floors';
import { stripTags } from './story';

/**
 * The game's voice menus outside a mission (docs/design/voice-control.md), in one place so the
 * pages, the classifier's training export (prompts.eval.ts) and its tests use the same options.
 */

const NUMBER_WORDS: Readonly<Record<number, string>> = {
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
};

/** The elevator: each open floor (by number, foe or name), Alex's Lab once open, the agent. */
export function elevatorMenu(open: readonly boolean[], labOpen: boolean): Menu {
  return {
    question: 'Where to, Agent? A floor, Alex’s Lab, or your agent?',
    options: [
      ...floors.flatMap((floor, i): MenuOption[] =>
        open[i]
          ? [
              {
                id: floor.id,
                label: `Floor ${String(floor.number)}: ${floor.foe.codename.toLowerCase()}`,
                says: [
                  `floor ${String(floor.number)}`,
                  NUMBER_WORDS[floor.number] ?? String(floor.number),
                  floor.foe.codename.toLowerCase(),
                  ...floor.foe.codename.toLowerCase().split(' ').slice(-1),
                ],
              },
            ]
          : [],
      ),
      ...(labOpen
        ? [{ id: '@lab', label: 'Alex’s Lab', says: ['lab', 'alex', 'scope', 'notebook'] }]
        : []),
      { id: '@agent', label: 'My agent', says: ['my agent', 'agent', 'my look', 'change me'] },
    ],
  };
}

/** Alex's Lab, at a notebook page: check it, or go back. */
export const LAB_START: Menu = {
  question: 'Ready to check this page?',
  options: [
    {
      id: 'go',
      label: 'Check it',
      says: ['yes', 'yeah', 'go', 'check', 'ready', 'ok', 'okay', 'start', 'lets go'],
    },
    {
      id: 'leave',
      label: 'Back to the elevator',
      says: ['elevator', 'leave', 'back', 'no', 'later'],
    },
  ],
};

/** The palette a layer's colour step offers. */
export function paletteFor(layer: LayerId) {
  return layer === 'hair' ? HAIR_COLOURS : GEAR_COLOURS;
}

/** A builder step's options (the tiles show the same), from the items given. */
export function builderOptions(
  step: Step,
  names: readonly string[],
  items: (layer: LayerId) => readonly LookItem[] = (layer) => ITEMS[layer],
): MenuOption[] {
  if (step.kind === 'codename')
    return names.map((n) => ({
      id: n,
      label: n,
      says: [n.toLowerCase(), ...n.toLowerCase().split(' ')],
    }));
  if (step.kind === 'colour')
    return paletteFor(step.layer).map((c) => ({ id: c.id, label: c.label, says: c.says }));
  return items(step.layer).map((i) => ({ id: i.id, label: i.label, says: i.says }));
}

/** A builder step's whole menu: its options, then next, back and surprise (more names, last). */
export function builderMenu(
  step: Step,
  band: 'younger' | 'older',
  names: readonly string[],
  items?: (layer: LayerId) => readonly LookItem[],
): Menu {
  const nav: MenuOption[] =
    step.kind === 'codename' ? [NAV.more, NAV.back] : [NAV.next, NAV.back, NAV.surprise];
  return {
    question: stripTags(STEP_LINES[band][stepKey(step)] ?? ''),
    options: [...builderOptions(step, names, items), ...nav],
  };
}

/**
 * The voice menus the classifier is trained and tested on, by context name (prompts.eval.ts,
 * compare.eval.ts, answers.eval.ts): every builder step with every item, the elevator with every
 * floor open, the lab's start and the check-back.
 */
export function trainingMenus(): Record<string, Menu> {
  return {
    ...allBuilderMenus('younger'),
    elevator: elevatorMenu(
      floors.map(() => true),
      true,
    ),
    'lab:start': LAB_START,
    confirm: YES_NO,
  };
}

/** Every builder step's menu with every item, for the classifier's training export. */
export function allBuilderMenus(band: 'younger' | 'older'): Record<string, Menu> {
  return Object.fromEntries(
    STEPS.map((s) => [`builder:${stepKey(s)}`, builderMenu(s, band, CODENAMES.slice(0, 4))]),
  );
}
